import { NEED_IDS, type NeedId } from './needs.ts';

export type BillingUnit = 'person' | 'household';
export type PricingConfidence = 'verified' | 'range' | 'quote-required';

export type AddOn = {
  id: string;
  label: string;
  annual: number | null;
  annualHigh?: number | null;
  covers: NeedId[];
};

export type Plan = {
  id: string;
  label: string;
  monthly: number | null;
  monthlyHigh?: number | null;
  annual: number | null;
  annualHigh?: number | null;
  billingUnit: BillingUnit;
  includedNeeds: NeedId[];
  addOns: AddOn[];
};

export type AppPricing = {
  slug: string;
  name: string;
  shortName: string;
  plans: Plan[];
  freeTier: boolean;
  priceRange: boolean;
  sourceUrl: string;
  verifiedAt: string;
  confidence: PricingConfidence;
  notes: string[];
};

export type CostRange = {
  low: number;
  high: number;
};

export type BillingCycle = 'monthly' | 'annual';

export function formatUsd(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  const hasCents = Math.abs(rounded - Math.round(rounded)) > 0.005;
  return rounded.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  });
}

export function formatCostRange(range: CostRange): string {
  if (Math.abs(range.high - range.low) < 0.01) return formatUsd(range.low);
  return `${formatUsd(range.low)}–${formatUsd(range.high)}`;
}

export function formatWholeDollarRange(range: CostRange): string {
  const low = Math.round(range.low);
  const high = Math.round(range.high);
  if (low === high) return formatUsd(low);
  return `${formatUsd(low)}–${formatUsd(high)}`;
}

export function midpoint(range: CostRange): number {
  return (range.low + range.high) / 2;
}

export function planUnits(plan: Plan, payers: number): number {
  return plan.billingUnit === 'person' ? payers : 1;
}

export function annualCostForPlan(
  plan: Plan,
  payers: number,
  billing: BillingCycle
): CostRange | null {
  const units = planUnits(plan, payers);
  if (billing === 'annual' && plan.annual !== null) {
    return { low: plan.annual * units, high: (plan.annualHigh ?? plan.annual) * units };
  }
  if (plan.monthly !== null) {
    return {
      low: plan.monthly * 12 * units,
      high: (plan.monthlyHigh ?? plan.monthly) * 12 * units,
    };
  }
  if (plan.annual !== null) {
    return { low: plan.annual * units, high: (plan.annualHigh ?? plan.annual) * units };
  }
  return null;
}

export function annualCostForAddOn(addOn: AddOn): CostRange | null {
  if (addOn.annual === null) return null;
  return { low: addOn.annual, high: addOn.annualHigh ?? addOn.annual };
}

function unitLabel(unit: BillingUnit): string {
  return unit === 'person' ? 'per parent' : 'per family';
}

function lowestPlanByMonthly(plans: Plan[]): Plan {
  return plans.reduce((cheapest, plan) => {
    const cheapestMonthly = cheapest.monthly ?? Number.POSITIVE_INFINITY;
    const planMonthly = plan.monthly ?? Number.POSITIVE_INFINITY;
    return planMonthly < cheapestMonthly ? plan : cheapest;
  });
}

export function formatPricingMonthly(app: AppPricing): string {
  const monthlyPlans = app.plans.filter(
    (plan): plan is Plan & { monthly: number } => plan.monthly !== null
  );
  if (monthlyPlans.length === 0) {
    return 'Sold mainly as annual planning software, not a monthly messenger';
  }

  const unit = unitLabel(monthlyPlans[0].billingUnit);
  const cheapest = lowestPlanByMonthly(monthlyPlans);
  const cheapestRange: CostRange = {
    low: cheapest.monthly as number,
    high: cheapest.monthlyHigh ?? (cheapest.monthly as number),
  };
  const higher = monthlyPlans.filter((plan) => plan.id !== cheapest.id);

  if (higher.length > 0) {
    const higherLow = Math.min(...higher.map((plan) => plan.monthly as number));
    const higherHigh = Math.max(
      ...higher.map((plan) => plan.monthlyHigh ?? (plan.monthly as number))
    );
    return `From about ${formatCostRange(cheapestRange)}/month ${unit}; higher tiers ${formatCostRange({
      low: higherLow,
      high: higherHigh,
    })}/month`;
  }

  const prefix = cheapestRange.low === cheapestRange.high ? 'About' : 'Commonly';
  return `${prefix} ${formatCostRange(cheapestRange)}/month ${unit}`;
}

export function twoParentAnnualRange(app: AppPricing): CostRange | null {
  const costs = app.plans
    .map((plan) => annualCostForPlan(plan, 2, 'annual'))
    .filter((range): range is CostRange => range !== null);
  if (costs.length === 0) return null;
  return {
    low: Math.min(...costs.map((range) => range.low)),
    high: Math.max(...costs.map((range) => range.high)),
  };
}

export function formatPricingAnnual(app: AppPricing): string {
  const range = twoParentAnnualRange(app);
  if (!range) return 'Annual pricing requires a quote';
  if (
    app.plans.every((plan) => plan.annual === null) ||
    app.confidence === 'quote-required'
  ) {
    return `Commonly about ${formatWholeDollarRange(range)}/year depending on package`;
  }
  if (app.plans.every((plan) => plan.billingUnit === 'household')) {
    return `Often about ${formatWholeDollarRange(range)}/year for the household`;
  }
  return `Typically ${formatWholeDollarRange(range)}/year for two parents on the modeled tiers`;
}

const HARBOR_NEEDS: NeedId[] = NEED_IDS.filter((id) => id !== 'recorded-calls');

export function buildHarborPricing(
  monthlyPrice: number,
  annualPrice: number,
  sourceUrl: string,
  verifiedAt: string
): AppPricing {
  return {
    slug: 'harbor',
    name: 'Harbor',
    shortName: 'Harbor',
    plans: [
      {
        id: 'harbor-standard',
        label: 'Harbor',
        monthly: monthlyPrice,
        annual: annualPrice,
        billingUnit: 'person',
        includedNeeds: HARBOR_NEEDS,
        addOns: [],
      },
    ],
    freeTier: false,
    priceRange: false,
    sourceUrl,
    verifiedAt,
    confidence: 'verified',
    notes: ['7-day trial. Billing is per person.'],
  };
}

export const COMPETITOR_PRICING = {
  ourfamilywizard: {
    slug: 'ourfamilywizard',
    name: 'OurFamilyWizard',
    shortName: 'OFW',
    plans: [
      {
        id: 'ofw-essentials',
        label: 'Essentials',
        monthly: 12.5,
        annual: 110,
        annualHigh: 150,
        billingUnit: 'person',
        includedNeeds: ['messaging', 'message-search', 'expenses', 'calendar', 'exports'],
        addOns: [],
      },
      {
        id: 'ofw-tone',
        label: 'Higher tier with ToneMeter',
        monthly: 25,
        annual: 150,
        annualHigh: 300,
        billingUnit: 'person',
        includedNeeds: [
          'messaging',
          'message-search',
          'expenses',
          'calendar',
          'tone-check',
          'exports',
        ],
        addOns: [],
      },
    ],
    freeTier: false,
    priceRange: true,
    sourceUrl: 'https://www.ourfamilywizard.com/pricing',
    verifiedAt: '2026-08-17',
    confidence: 'range',
    notes: ['ToneMeter and writing assistance sit on higher tiers.'],
  },
  talkingparents: {
    slug: 'talkingparents',
    name: 'TalkingParents',
    shortName: 'TalkingParents',
    plans: [
      {
        id: 'tp-essentials',
        label: 'Essentials',
        monthly: 7,
        annual: 84,
        billingUnit: 'person',
        includedNeeds: ['messaging', 'message-search', 'expenses', 'calendar', 'exports'],
        addOns: [],
      },
      {
        id: 'tp-recorded',
        label: 'Higher tier with recorded calls',
        monthly: 16,
        monthlyHigh: 32,
        annual: 192,
        annualHigh: 384,
        billingUnit: 'person',
        includedNeeds: [
          'messaging',
          'message-search',
          'expenses',
          'calendar',
          'exports',
          'recorded-calls',
        ],
        addOns: [],
      },
    ],
    freeTier: false,
    priceRange: true,
    sourceUrl: 'https://talkingparents.com/pricing',
    verifiedAt: '2026-08-17',
    confidence: 'range',
    notes: ['Recorded calls and transcription sit on higher tiers.'],
  },
  appclose: {
    slug: 'appclose',
    name: 'AppClose',
    shortName: 'AppClose',
    plans: [
      {
        id: 'appclose-standard',
        label: 'AppClose',
        monthly: 7.99,
        monthlyHigh: 8.99,
        annual: 95.88,
        annualHigh: 107.88,
        billingUnit: 'person',
        includedNeeds: ['messaging', 'message-search', 'expenses', 'calendar', 'exports'],
        addOns: [],
      },
    ],
    freeTier: false,
    priceRange: true,
    sourceUrl: 'https://appclose.com',
    verifiedAt: '2026-08-17',
    confidence: 'range',
    notes: ['Web is about $7.99/month; in-app is about $8.99/month. Hardship waivers exist.'],
  },
  '2houses': {
    slug: '2houses',
    name: '2houses',
    shortName: '2houses',
    plans: [
      {
        id: '2houses-family',
        label: 'Family plan',
        monthly: 9.99,
        monthlyHigh: 12.99,
        annual: 99,
        billingUnit: 'household',
        includedNeeds: ['messaging', 'expenses', 'calendar', 'exports'],
        addOns: [],
      },
    ],
    freeTier: false,
    priceRange: true,
    sourceUrl: 'https://www.2houses.com/en/pricing',
    verifiedAt: '2026-08-17',
    confidence: 'range',
    notes: ['One family subscription can cover both households.'],
  },
  'custody-x-change': {
    slug: 'custody-x-change',
    name: 'Custody X Change',
    shortName: 'Custody X',
    plans: [
      {
        id: 'cxc-package',
        label: 'Annual planning package',
        monthly: null,
        annual: 147,
        annualHigh: 247,
        billingUnit: 'household',
        includedNeeds: ['messaging', 'message-search', 'expenses', 'calendar', 'exports'],
        addOns: [],
      },
    ],
    freeTier: false,
    priceRange: true,
    sourceUrl: 'https://www.custodyxchange.com',
    verifiedAt: '2026-08-17',
    confidence: 'range',
    notes: ['Sold as an annual planning package, not a monthly messenger.'],
  },
} satisfies Record<string, AppPricing>;

export const COMPETITOR_PRICING_APPS: AppPricing[] = Object.values(COMPETITOR_PRICING);
